import { Assets, Graphics, Texture, type Renderer } from 'pixi.js';
import type { ShapeName } from './types';

/**
 * 스프라이트 로더.
 * sprite 경로가 비었거나 로드에 실패하면 흰색 도형 텍스처로 대체한다.
 * 도형은 전부 흰색으로 만들고 Sprite.tint 로 색을 입히므로,
 * 모양 하나당 텍스처 하나만 있으면 된다 = 드로우콜이 한 덩어리로 묶인다.
 */

const SHAPE_R = 32; // 도형 텍스처 기준 반지름 (실제 크기는 sprite.scale로 맞춘다)

const shapeCache = new Map<ShapeName, Texture>();
const fileCache = new Map<string, Texture | null>();

function drawShape(g: Graphics, shape: ShapeName, r: number): void {
  const cx = r;
  const cy = r;
  switch (shape) {
    case 'circle':
      g.circle(cx, cy, r);
      break;
    case 'square':
      g.rect(cx - r * 0.82, cy - r * 0.82, r * 1.64, r * 1.64);
      break;
    case 'triangle':
      g.poly([cx, cy - r, cx + r * 0.92, cy + r * 0.75, cx - r * 0.92, cy + r * 0.75]);
      break;
    case 'diamond':
      g.poly([cx, cy - r, cx + r * 0.78, cy, cx, cy + r, cx - r * 0.78, cy]);
      break;
    case 'hexagon': {
      const pts: number[] = [];
      for (let i = 0; i < 6; i++) {
        const a = (Math.PI / 3) * i - Math.PI / 2;
        pts.push(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
      }
      g.poly(pts);
      break;
    }
    case 'star': {
      const pts: number[] = [];
      for (let i = 0; i < 10; i++) {
        const a = (Math.PI / 5) * i - Math.PI / 2;
        const rr = i % 2 === 0 ? r : r * 0.45;
        pts.push(cx + Math.cos(a) * rr, cy + Math.sin(a) * rr);
      }
      g.poly(pts);
      break;
    }
    case 'cross': {
      const t = r * 0.34;
      g.poly([
        cx - t, cy - r, cx + t, cy - r, cx + t, cy - t, cx + r, cy - t,
        cx + r, cy + t, cx + t, cy + t, cx + t, cy + r, cx - t, cy + r,
        cx - t, cy + t, cx - r, cy + t, cx - r, cy - t, cx - t, cy - t,
      ]);
      break;
    }
  }
  g.fill(0xffffff);
}

/** 모양별 흰색 텍스처를 미리 구워둔다. app.init() 직후 한 번만 부른다. */
export function bakeShapeTextures(renderer: Renderer): void {
  const shapes: ShapeName[] = ['circle', 'square', 'triangle', 'diamond', 'star', 'hexagon', 'cross'];
  for (const shape of shapes) {
    const g = new Graphics();
    drawShape(g, shape, SHAPE_R);
    const tex = renderer.generateTexture({ target: g, resolution: 2, antialias: true });
    tex.source.scaleMode = 'nearest';
    shapeCache.set(shape, tex);
    g.destroy();
  }
}

export function shapeTexture(shape: ShapeName): Texture {
  return shapeCache.get(shape) ?? Texture.WHITE;
}

/**
 * 스프라이트 경로들을 미리 로드한다. 실패한 경로는 null로 기록하고 조용히 넘어간다.
 * (이미지가 아직 없어도 게임은 도형으로 그대로 돌아간다)
 */
export async function preloadSprites(paths: Array<string | undefined>): Promise<void> {
  const targets = Array.from(new Set(paths.filter((p): p is string => !!p && p.trim() !== '')));
  await Promise.all(
    targets.map(async (p) => {
      if (fileCache.has(p)) return;
      try {
        const tex = (await Assets.load(p)) as Texture;
        // 도트 그림은 반드시 최근접 확대
        tex.source.scaleMode = 'nearest';
        tex.source.autoGenerateMipmaps = false;
        fileCache.set(p, tex);
      } catch {
        console.warn(`[assets] 스프라이트를 못 찾아 도형으로 대체: ${p}`);
        fileCache.set(p, null);
      }
    }),
  );
}

export interface Visual {
  texture: Texture;
  /** 도형 대체본이면 색을 tint로 입혀야 한다. 실제 이미지면 0xffffff. */
  tint: number;
  /** 텍스처 기준 반지름 (scale 계산용) */
  baseRadius: number;
}

export function hexToNumber(hex: string): number {
  const s = hex.trim().replace(/^#/, '').replace(/^0x/i, '');
  const n = parseInt(s, 16);
  return Number.isFinite(n) ? n : 0xffffff;
}

/** 스프라이트가 있으면 그걸, 없으면 도형+색을 돌려준다. */
export function resolveVisual(sprite: string | undefined, shape: ShapeName, color: string): Visual {
  if (sprite && sprite.trim() !== '') {
    const tex = fileCache.get(sprite);
    if (tex) return { texture: tex, tint: 0xffffff, baseRadius: Math.max(tex.width, tex.height) / 2 };
  }
  return { texture: shapeTexture(shape), tint: hexToNumber(color), baseRadius: SHAPE_R };
}
